import React, { useEffect, useMemo, useState } from "react";
import "./AddSupplierForm.css";

function normalizeRoles(user) {
  const roles = Array.isArray(user?.roles) ? user.roles.slice() : [];
  if (roles.length === 0 && user?.role) roles.push(user.role);
  return roles.map((r) => String(r).trim().toLowerCase()).filter(Boolean);
}

function hasRole(user, role) {
  return normalizeRoles(user).includes(String(role || "").toLowerCase());
}

function AddSupplierForm({ isEditing, currentSupplier, onSave, onCancel, user }) {
  const isDeveloper = useMemo(() => hasRole(user, "developer"), [user]);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [tenantId, setTenantId] = useState(user?.tenantId || "");

  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (isEditing && currentSupplier) {
      setName(currentSupplier.name || "");
      setEmail(currentSupplier.email || "");
      setPhone(currentSupplier.phone || "");
      setTenantId(currentSupplier.tenantId || user?.tenantId || "");
    } else {
      setName("");
      setEmail("");
      setPhone("");
      setTenantId(user?.tenantId || "");
    }
    setTouched(false);
  }, [isEditing, currentSupplier, user]);

  const payload = useMemo(() => {
    const base = {
      name: String(name || "").trim(),
      email: String(email || "").trim(),
      phone: String(phone || "").trim(),
    };
    return isDeveloper ? { ...base, tenantId: String(tenantId || "").trim() } : base;
  }, [name, email, phone, isDeveloper, tenantId]);

  const errors = useMemo(() => {
    const out = {};
    if (!payload.name) out.name = "Nome obbligatorio.";
    if (payload.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) out.email = "Email non valida.";
    if (isDeveloper && !payload.tenantId) out.tenantId = "Tenant obbligatorio per developer.";
    return out;
  }, [payload, isDeveloper]);

  const canSubmit = Object.keys(errors).length === 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    onSave?.(payload);
  };

  return (
    <form className="asForm" onSubmit={handleSubmit} noValidate>
      <div className="asGrid">
        <label className="asField">
          <span className="asLabel">
            Nome <span className="asReq">*</span>
          </span>
          <input
            className={`asInput ${touched && errors.name ? "is-invalid" : ""}`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Es. Ferramenta Rossi Srl"
            autoComplete="organization"
            required
          />
          {touched && errors.name ? <div className="asError">{errors.name}</div> : null}
        </label>

        <label className="asField">
          <span className="asLabel">Email</span>
          <input
            className={`asInput ${touched && errors.email ? "is-invalid" : ""}`}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="esempio@email.it"
            autoComplete="email"
          />
          {touched && errors.email ? <div className="asError">{errors.email}</div> : null}
        </label>

        <label className="asField">
          <span className="asLabel">Telefono</span>
          <input
            className="asInput"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+39 333 1234567"
            autoComplete="tel"
            inputMode="tel"
          />
        </label>

        {isDeveloper && (
          <label className="asField asField--full">
            <span className="asLabel">
              Tenant <span className="asReq">*</span>
            </span>
            <input
              className={`asInput ${touched && errors.tenantId ? "is-invalid" : ""}`}
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              placeholder="tenantId (es. savona)"
            />
            <span className="asHint">Solo developer: puoi scegliere il tenant del fornitore.</span>
            {touched && errors.tenantId ? <div className="asError">{errors.tenantId}</div> : null}
          </label>
        )}
      </div>

      <div className="asActions">
        <button type="button" className="asBtn asBtn--ghost" onClick={onCancel}>
          Annulla
        </button>

        <button type="submit" className="asBtn asBtn--primary" disabled={!canSubmit && touched}>
          {isEditing ? "Salva modifiche" : "Crea fornitore"}
        </button>
      </div>
    </form>
  );
}

export default AddSupplierForm;
